import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

type ProductionDeclarationOrderBannerProps = {
  purchaseOrderName?: string | null;
  supplierName?: string | null;
  actionLabel?: string;
  actionDisabled?: boolean;
  actionOnClick?: () => void;
  actionTo?: string;
  actionSlot?: ReactNode;
  actionDescription?: ReactNode;
  productionDeclarationId: string;
  token?: string;
};

export function ProductionDeclarationOrderBanner({
  purchaseOrderName,
  supplierName,
  actionLabel,
  actionDisabled = false,
  actionOnClick,
  actionTo = '/production-declarations/$id',
  actionSlot,
  actionDescription,
  productionDeclarationId,
  token,
}: ProductionDeclarationOrderBannerProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  const resolvedPurchaseOrderName =
    purchaseOrderName?.trim() || t('orderBanner.purchaseOrderFallbackName');
  const resolvedSupplierName =
    supplierName?.trim() || t('orderBanner.supplierFallbackName');
  const hasLinkAction = Boolean(actionLabel && actionTo && !actionOnClick);
  const hasButtonAction = Boolean(actionLabel && actionOnClick);

  return (
    <Card className='rounded-2xl border-declaration-border bg-white py-0 text-declaration-foreground shadow-none'>
      <CardContent className='flex flex-col gap-4 p-6'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='space-y-1'>
            <p className='font-semibold text-2xl tracking-tight'>
              {resolvedPurchaseOrderName}
            </p>
            <p className='text-declaration-foreground-muted text-sm'>
              {resolvedSupplierName}
            </p>
          </div>
          {actionSlot || hasLinkAction || hasButtonAction ? (
            <div className='flex shrink-0 justify-end'>
              {actionSlot}
              {!actionSlot && hasLinkAction ? (
                <Button
                  asChild
                  disabled={actionDisabled}
                  variant='declaration-primary'
                >
                  <Link
                    params={{ id: productionDeclarationId }}
                    search={{ token }}
                    to={actionTo}
                  >
                    {actionLabel}
                  </Link>
                </Button>
              ) : null}
              {!actionSlot && hasButtonAction ? (
                <Button
                  disabled={actionDisabled}
                  onClick={actionOnClick}
                  type='button'
                  variant='declaration-primary'
                >
                  {actionLabel}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {actionDescription ? (
          <p className='text-declaration-foreground-muted text-sm'>
            {actionDescription}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
